import { isServer } from "./utils/isServer";
import { validatePhoneNumber } from "./utils/validatePhoneNumber";
import { RequestHeaders } from "api";
import { logger } from "logging";
import { loggingService } from "services/loggingService";
import { platformNames } from "shared/constants/index";
import { DEFAULT_API_TIMEOUT_IN_MS } from "../api/constants";
import { checkIsEddSwitchEnabled } from "wrapper/ExperimentAndSwitchWrapper/helpers";
import { getIcons, ICONS_NAMES } from "./utils/getIcons";
import { checkIsBeautyPlatform } from "./utils/redirectToCheckoutPage";
import { PaymentOfferRule } from "logics/src/utils/@types";
import { PAYMENT_MODES } from "constants/paymentModes";
import { getCookie } from "./utils/cookie";
import { HEADER_FOOTER_MIGRATION_MY_SAVED_PAYMENT, HEADER_FOOTER_MIGRATION_PAYMENT_SUCCESS } from "constants/cookies";


/**
 * @description - returns remoteconfig object
 */
 export const getRemoteConfig = () => {
  if (isServer()) return;
  return (window as any)?.remoteConfig;
};

/**
 * @description - returns api timeout time for api to respond,
 * if response in not returned in that interval timeout error will be thrown
 */
export const getApiTimeoutInMs = () => {
  try {
    const remoteConfig = getRemoteConfig();
    /** default timeout of 10 seconds */
    let apiTimeout = remoteConfig?.configs?.apiTimeoutInMs || DEFAULT_API_TIMEOUT_IN_MS;
    return apiTimeout;
  } catch (error) {
    logger.error(error, "error in getApiTimeoutInMs");
    return DEFAULT_API_TIMEOUT_IN_MS;
  }
};

/**
 *@description getCustomerFirstName function that takes name as params
                and returns only first name if passed name is correct string.
 * @param name customerName is to be passed as params
 * @returns only first name of the customer name
 */
export const getCustomerFirstName = (name: string) => {
  try {
    if (name) {
      const splittedName = name.split(" ");
      if (splittedName && splittedName.length) {
        return splittedName[0];
      }
    }
  } catch (error) {
    loggingService.error(error);
    logger.error(error, "Error in getCustomerFirstName");
  }

  return name;
};

/**
     * Generates uuidv4
     * @returns string
     */
function generate_uuidv4() {
  var dt = new Date().getTime();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g,
  function( c ) {
     var rnd = Math.random() * 16;//random number in range 0 to 16
     rnd = (dt + rnd) % 16 | 0;
     dt = Math.floor(dt / 16);
     return (c === "x" ? rnd : (rnd & 0x3 | 0x8)).toString(16);
  });
}

/**
 *  generates random unique identifier
 */
export const getUniqueIdentifier = () => {
  let uniqueIdentifier = "";
  if (typeof window !== "undefined") {
    try {
      uniqueIdentifier = crypto.randomUUID();
    } catch (error) {
      // error is thrown if crypto is not supported(it is not supported in older browsers)
      uniqueIdentifier = generate_uuidv4();
    }
  }
  return uniqueIdentifier;
};


export const getConfigExpiry = () => {
  const remoteConfig:any = getRemoteConfig();
  return remoteConfig?.configs?.remoteConfigExpiryInMs;
};

/**
 * @description - returns the platform
 */
export const getPlatform = () => {
  switch (__PLATFORM__) {
    case platformNames.FASHION:
      return platformNames.FASHION;
    case platformNames.MEN:
      return platformNames.MEN;
    case platformNames.NYKAA:
      return platformNames.NYKAA;
    case platformNames.FOOTLOCKER:
      return platformNames.FOOTLOCKER;
    default:
      return platformNames.NYKAAD;
  }
};

/**
 *@description isPhoneNumber function that takes a string as param
                and returns true/false based on if passed string is phone number or not.
 * @param phone value of email or phone number is to be passed as params
 * @returns boolean
 */
export const isPhoneNumber = (phone: string) => {
  const isPhone = validatePhoneNumber(phone);
  return (!isPhone.hasError);
};


/**
 * @description
 * calls setFormKey function in RequestHeaders
 * receives formKey and calls setFormKey
 * @param formKey {string} - formKey from ReactConfig api response
 */
export const setCsrfToken = (formKey: string) => {
  /**
   * when csrf token is set then remove shimmer;
   */
  removeShimmer();
  RequestHeaders.setFormKey(formKey);
};


export const removeShimmer = () => {
  if (!isServer()) {
    let shimmer = document.getElementById("load-shimmer");
    shimmer?.remove();
  }
};

/*
 * general function to add queryParams
 */
export const doAppendQueryParam = (key: string, value: string) => {
    let pathName = window.location.pathname;
    let search = window.location.search;

    if (search) {
      pathName = pathName + search + `&${key}=${value}`;
    } else {
      pathName = pathName + `?${key}=${value}`;
    }
    return pathName;
};

/**
 * function to return serviceable header base  on platform
 */
export const getServiceableHeader = () => {
  let headers:any = {};

  if (!checkIsBeautyPlatform()) {
    headers["X-SERVICEABLE_V1"] = checkIsEddSwitchEnabled();
  } else {
    headers["X-SERVICEABLE"] = checkIsEddSwitchEnabled();
  }

  return headers;
};
/*
 * general function to add queryParams if it doesn't exist in the url,
 * but if it exists then it will replace with new value.
 * eg: /v2/payment?screen=newPayment => doAppendOrReplaceQueryParam('screen', 'newPayment') => v2/payment?screen=newPayment
 * eg: /v2/payment => doAppendOrReplaceQueryParam('screen', 'newPayment') => v2/payment?screen=newPayment
 */
export const doAppendOrReplaceQueryParam = (key: string, value: string) => {
  try {
    const pathName = window.location.pathname;
    const search = window.location.search;

    const searchParams = new URLSearchParams(search);
    searchParams.set(key, value);
    const newPath = pathName + "?" + searchParams.toString();

    return newPath;
  } catch (error) {
    console.log("Error in doAppendOrReplaceQueryParam");
  }
};
/**
 * discard a callback for some time if it is called during that time.
 * only call the callback if it is called again after that time.
 */
export const discardCallbackWithTimeout = (callback: Function, timeout: number) => {
  let isTimeoutSet = false;
  setTimeout(() => {
    isTimeoutSet = true;
  }, timeout);
  return (...args: any) => {
    if (!isTimeoutSet) return;
    callback(...args);
  };
};

/**
 * Determine the mobile operating system.
 * This function returns one of 'iOS', 'Android', or 'unknown'.
 *
 * @returns {String}
 */
 export const getMobileOperatingSystem = () => {
  //android detection
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  if (/android/i.test(userAgent)) {
      return "Android";
  }

  // iOS detection
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      return "iOS";
  }

  return "unknown";
};


/**
 * Determine the mobile browser.
 * This function returns one of 'Opera', 'Edge', 'Chrome', 'Safari', 'Firefox' , or 'unknown'.
 *
 * @returns {String}
 */
export const getBrowserName = () => {
  let browserName = "";
  if ((navigator.userAgent.indexOf("Opera") || navigator.userAgent.indexOf("OPR")) != -1) {
    browserName = "Opera";
  } else if (navigator.userAgent.indexOf("Edg") != -1) {
    browserName = "Edge";
  } else if (navigator.userAgent.indexOf("Chrome") != -1) {
    browserName = "Chrome";
  } else if (navigator.userAgent.indexOf("Safari") != -1) {
    browserName = "Safari";
  } else if (navigator.userAgent.indexOf("Firefox") != -1) {
    browserName = "Firefox";
  } else {
    browserName = "unknown";
  }

  return browserName;
};

/* @description It is for getting the brand logo from remote config, if switch is enabled then
* fetch from remoteConfig o/w get from local getIcons function.
* @param platform
*/
export const getBrandLogo = (platform: string) => {
  try {
    const remoteConfig = getRemoteConfig();
    const brandLogoConfig = remoteConfig?.switches?.brandLogo;
    let brandLogo = getIcons(platform, ICONS_NAMES.HEADER_LOGO);
    if (brandLogoConfig?.status) {
      brandLogo = brandLogoConfig.iconURL;
    }
    return brandLogo;
  } catch (e) {
    logger.error(e, "Error in getBrandLogo");
  }
  return getIcons(platform, ICONS_NAMES.HEADER_LOGO);
};

export const getAbsoluteURL = (path: string) => {
  let origin = "";
  if (!isServer()) {
    origin = window.location.origin;
  }
  return `${origin}${path}`;
};

export const formatNumber = (num: number) => {
  // Check if the number has a decimal part
  if (num % 1 !== 0) {
      // If it has a decimal part, format to two decimal places using toFixed
      return num.toFixed(2);
  } else {
      // If no decimal part, return the number as it is
      return num;
  }
};

export const getPriorityBasedSortedOffers = (cartRules: any) => {
  let sortedPaymentRules: any = cartRules?.paymentRules.filter(
    (rule: PaymentOfferRule) => rule.type !== "cashback"
  );

  // We are sorting the offer based on the priority.
  sortedPaymentRules = sortedPaymentRules.sort(
    (a: PaymentOfferRule, b: PaymentOfferRule) => b?.priority - a?.priority
  );
  return sortedPaymentRules;
}

/**
 * function to get payment option for address page.
 */
export const getSelectedPaymentOptionForAddresPage = () => {
  try {
    const remoteConfig = getRemoteConfig();
    return remoteConfig.configs.payment.defaultPaymentOptionForAddressPage;
  } catch (error) {
    return PAYMENT_MODES.COD;
  }
};

export const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const hasHeaderFooterExperimentEnabledForSavedPayment = () => {
  return getCookie(HEADER_FOOTER_MIGRATION_MY_SAVED_PAYMENT) === "1";
};

export const hasHeaderFooterExperimentEnabledForPaymentSuccess = () => {
  return getCookie(HEADER_FOOTER_MIGRATION_PAYMENT_SUCCESS) === "1";
};
export const disableBodyScroll = function(isVisible: boolean) {
  try {
    /**
     * to avoid background scroll
     */
    if (document) {
      const bodySelector = document.querySelector("body") as any;

      if (!isVisible) {
        bodySelector.setAttribute("style", "");
      } else {
        bodySelector.setAttribute("style", "overflow: hidden");
      }
    }
  } catch (error) {
    /**
     * in case of error removing body overflow.
     */
    if (document) {
      const bodySelector = document.querySelector("body") as any;
      bodySelector.setAttribute("style", "");
    }
  }
};

/**
 * @description - Checks if the checkout revamp experiment is enabled for the user
 * by reading the EXP_AB_CHECKOUT_REVAMP cookie value.
 * @returns {boolean} - Returns true if cookie value is "A" (experiment variant), false otherwise
 * @todo Remove this function after 100% rollout of checkout revamp experiment
 */
export const checkIsCheckoutRevampEnabled = () => {
    return getCookie("EXP_AB_CHECKOUT_REVAMP") === "A";
};